import { User } from 'firebase/auth';
import { getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { logger } from './logger';
import { userProfileDocument } from '../lib/paths';

/**
 * Only accounts created in the last few minutes are provisioned.
 *
 * Without this an account that predates the legacyUser backfill — no claims,
 * and old enough to have no profile document — would be handed
 * subscriptionStatus 'pending' on its next sign-in and pushed into onboarding
 * behind the paywall. The window keeps that impossible whatever order
 * environments are deployed in, at the cost of not healing an account that was
 * orphaned and then left alone for longer than this.
 */
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;

/**
 * Creates the userProfiles document for a freshly created account that does
 * not have one.
 *
 * Registration writes this document itself, but the account already exists in
 * Firebase Auth by then: if that write fails the user is left signed in with
 * no profile, and SubscriptionContext reads a missing document as 'legacy', so
 * OnboardingGuard waves them past company details and the trial into an app
 * whose every write the rules deny. This closes that gap, and gives any future
 * sign-in method that skips the registration form (Google, for one) a single
 * place to provision through.
 *
 * Terms and privacy acceptance are deliberately left unset — consent belongs
 * to the person, not to this function, and MigrationHandler already prompts
 * for it whenever the recorded version does not match the current one.
 *
 * @returns true when a profile was created.
 */
export async function ensureUserProfile(user: User): Promise<boolean> {
  try {
    // Grandfathered and paying accounts are provisioned already. Their claims
    // are the reliable signal — a legacy account is allowed to have no
    // subscriptionStatus, so the document alone cannot tell them apart.
    const { claims } = await user.getIdTokenResult();
    if (claims.legacyUser === true || claims.subscriptionActive === true) {
      return false;
    }

    const createdAt = user.metadata.creationTime
      ? Date.parse(user.metadata.creationTime)
      : NaN;
    if (
      !Number.isFinite(createdAt) ||
      Date.now() - createdAt > NEW_ACCOUNT_WINDOW_MS
    ) {
      return false;
    }

    const profileRef = userProfileDocument(user.uid);
    if ((await getDoc(profileRef)).exists()) {
      return false;
    }

    await setDoc(profileRef, {
      email: user.email ?? '',
      createdAt: Timestamp.now(),
      subscriptionStatus: 'pending',
      trialEndsAt: null,
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
    });

    logger.info('Created missing user profile', {
      component: 'ensureUserProfile',
      operation: 'create',
    });
    return true;
  } catch {
    // Never block sign-in on this. A user who gets in without a profile is no
    // worse off than before; one who cannot sign in at all is.
    logger.warn('Could not ensure user profile', undefined, {
      component: 'ensureUserProfile',
      operation: 'create',
    });
    return false;
  }
}
